import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import Header from '../components/Headers/Header';
import CustomLoader from '../components/Loader/CustomLoader';

import DataTable from 'react-data-table-component';
import orderBy from 'lodash/orderBy';
import { customStyles } from '../utils/tableStyleForNestPassCustomersPage';
import { Icon } from '@iconify/react'; 
import useGlobalStyles from '../utils/globalStyles';
import {
  Container,
  Button,
  Grid,
  Typography,
  Box,
  TextField,
} from '@mui/material';
import TableHeader from '../components/TableHeader';

// Define the GraphQL query for Nest Pass Purchased Customers
const GET_NEST_PASS_PURCHASEED_CUSTOMERS = gql`
  query GetNestPassPurchasedCustomersByDateRange($startDate: String!, $endDate: String!, $skip: Int!, $limit: Int!) {
    getNestPassPurchasedCustomersByDateRange(startDate: $startDate, endDate: $endDate, skip: $skip, limit: $limit) {
      nestPassPurchasedCustomers {
        customerName
        customerPhone
        purchaseDate
        expiryDate
        statusMessage
        amount
      }
      totalCount
      totalPurchaseAmount
      storeName
      nestPassDurationInMonths
      nestPassRegularPurchasePrice
      nestPassDiscountedPurchasePrice
    }
  }
`;

const NestPassCustomersPage = (props) => {
  const { t } = props; // Translation function from withTranslation HOC
  const [customersData, setCustomersData] = useState([]); // To hold customer data for the table
  const golbalClasses = useGlobalStyles();
  const [endDateError, setEndDateError] = useState('');

  // Format dates to YYYY-MM-DD for input fields
  const formatToYYYYMMDD = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Format date to ISO string for GraphQL variables
  const formatDateToISO = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // For start date, ensure it's the beginning of the day in UTC
    // For end date, ensure it's the end of the day in UTC
    // This example sets it to the start of the day. Adjust based on your backend's expectation.
    return date.toISOString();
  };

  const [page, setPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(page);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [formData, setFormData] = useState({
    start: '',
    end: ''
  });
  const [StartDate, setStartDate] = useState('');
  const [EndDate, setEndDate] = useState('');

  const client = useApolloClient(); // Apollo Client instance

  // Fetch customer data for the main table
  const [fetchCustomersData, { loading: customersLoading, error: customersError, data: customersQueryResult }] = useLazyQuery(GET_NEST_PASS_PURCHASEED_CUSTOMERS, {
    fetchPolicy: 'no-cache', // Important for fresh data on each fetch
    onCompleted: (data) => {
      if (data && data.getNestPassPurchasedCustomersByDateRange) {
        setCustomersData(data.getNestPassPurchasedCustomersByDateRange.nestPassPurchasedCustomers || []);
        setTableData(data.getNestPassPurchasedCustomersByDateRange.nestPassPurchasedCustomers || []); // For DataTable
        setTotalRows(data.getNestPassPurchasedCustomersByDateRange.totalCount || 0);
      }
    },
    onError: error => {
      console.error("Error fetching customer data:", error);
      const isForbiddenError = error && error.graphQLErrors && error.graphQLErrors.some(err => err.extensions.code === 'FORBIDDEN');
      if (isForbiddenError) {
        // Handle forbidden error, e.g., redirect to login
        // localStorage.removeItem('user-native');
        // localStorage.removeItem('restaurant_id');
        // client.clearStore();
        // props.history.push('/auth/login'); // Uncomment and ensure props.history is available if you use react-router
      }
    },
  });

  // Helper function to get status color for display
  function getStatusColor(statusMessage) {
    switch (statusMessage) {
      case 'Active': return "green";
      case 'Expired': return "red";
      case 'Pending': return "orange";
      default: return "black";
    }
  }

  const handleEndDateChange = (event) => {
    const newEndDate = event.target.value;
    if (StartDate && newEndDate < StartDate) {
      setEndDateError('End date cannot be less than start date.');
    } else {
      setEndDateError('');
      setEndDate(newEndDate);
      // For endDate, set to end of the day to ensure inclusive range
      const endOfDay = new Date(newEndDate);
      endOfDay.setHours(23, 59, 59, 999); // Set to end of day
      setFormData({ ...formData, end: endOfDay.toISOString() });
    }
  };

  const handleFetchStats = () => {
    if (formData.start && formData.end) {
      fetchCustomersData({
        variables: {
          startDate: formData.start,
          endDate: formData.end,
          limit: perPage,
          skip: (page - 1) * perPage,
        }
      });
    } else {
      // Using a console log for missing dates, replace with a Material-UI Snackbar/Dialog
      console.log('Please select both start and end dates.');
      // Example of a simple alert, replace with a proper UI notification
      alert('Please select both start and end dates.');
    }
  };

  const columns = [
    {
      name: 'SL No',
      selector: 'slNo',
      cell: (row, index) => <>{index + 1 + (page - 1) * perPage}</>,
      style: { cursor: 'pointer' },
    },
    {
      name: 'Customer Name',
      selector: 'customerName',
      cell: (row) => <>{row.customerName}</>,
      style: { cursor: 'pointer', minWidth: "150px" },
    },
    {
      name: 'Customer Phone',
      selector: 'customerPhone',
      cell: (row) => <>{row.customerPhone}</>,
      style: { cursor: 'pointer', minWidth: "150px" },
    },
    {
      name: 'Purchase Date',
      selector: 'purchaseDate',
      cell: (row) => <>{new Date(row.purchaseDate).toLocaleDateString()}</>,
      style: { cursor: 'pointer', minWidth: "150px" },
    },
    {
      name: 'Expiry Date',
      selector: 'expiryDate',
      cell: (row) => <>{new Date(row.expiryDate).toLocaleDateString()}</>,
      style: { cursor: 'pointer', minWidth: "150px" },
    },
    {
      name: 'Status',
      selector: 'statusMessage',
      cell: (row) => (
        <Typography sx={{
          color: getStatusColor(row.statusMessage), fontWeight: 600, fontSize: '13px',
        }}>
          {row.statusMessage}
        </Typography>
      ),
      style: { cursor: 'pointer', minWidth: "250px" }, // Increased minWidth for status
    },
    {
      name: 'Amount',
      selector: 'amount',
      cell: (row) => <>₹{row.amount}</>,
      style: { cursor: 'pointer' },
    },
  ];

  useEffect(() => {
    // Only fetch data if both start and end dates are set in formData
    if (formData.start && formData.end) {
        
      fetchCustomersData({
        variables: {
          startDate: formData.start,
          endDate: formData.end,
          limit: perPage,
          skip: (page - 1) * perPage,
        }
      });
    }
  },  [page, perPage, fetchCustomersData]);
  const handlePerRowChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1); // Reset to first page when perPage changes
  };

  const handlePageChange = (page) => {
    setPage(page);
    setCurrentPage(page);
  };

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field]) {
        return typeof row[field] === 'string' ? row[field].toLowerCase() : row[field];
      }
      return row[field];
    };
    return orderBy(rows, handleField, direction);
  };

  return (
    <>
      <Header />
      <Container className={golbalClasses.flex} sx={{ mt: 3, mb: 3, maxWidth: '1500px' }}> {/* Increased maxWidth */}
        <Box sx={{ mb: 3, mt: 3, width: '100%', maxWidth: '1000px' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Start Date Picker */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={"Select Start Date"}
                type="date"
                value={StartDate}
                onChange={(e) => {
                  const formattedStartDate = formatDateToISO(e.target.value);
                  setStartDate(e.target.value);
                  setFormData({ ...formData, start: formattedStartDate });
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  input: { color: 'black' },
                  '.MuiInputBase-root': { color: 'black' },
                  '& label.Mui-focused': { color: 'black' },
                }}
              />
            </Grid>

            {/* End Date Picker */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={"Select End Date"}
                type="date"
                value={EndDate}
                onChange={handleEndDateChange}
                error={!!endDateError}
                helperText={endDateError}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  input: { color: 'black' },
                  '.MuiInputBase-root': { color: 'black' },
                  '& label.Mui-focused': { color: 'black' },
                }}
              />
            </Grid>

            {/* Fetch Report Button */}
            <Grid item xs={12} sm={6} md={4}>
              <Button
                onClick={handleFetchStats}
                className={golbalClasses.dashbordBtn}
                fullWidth
                disabled={customersLoading || !formData.start || !formData.end}
              >
                <Icon icon="ic:twotone-plus" style={{ color: "white" }} /> {"Fetch Report"}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Loading and Error Messages */}
        {customersLoading ? (
          <CustomLoader />
        ) : customersError ? (
          <Box sx={{ width: '100%', maxWidth: '1000px', my: 2 }}>
            <Typography color="error" variant="body1" sx={{ p: 2, bgcolor: 'red.100', border: '1px solid red.400', borderRadius: '8px' }}>
              Error fetching data: {customersError.message || "An unknown error occurred."}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary Information */}
            {customersQueryResult && customersQueryResult.getNestPassPurchasedCustomersByDateRange && (
              <Box sx={{
                bgcolor: 'blue.50', p: 4, borderRadius: 'lg', mb: 3, boxShadow: 'md', width: '100%', maxWidth: '1000px',
                border: '1px solid #bfdbfe'
              }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'semibold', color: 'blue.800', mb: 2 }}>
                  {"Summary:"}
                </Typography>
                {/* Changed Grid layout for summary to 3 columns, 2 rows */}
                <Grid container spacing={2} sx={{ color: 'gray.700' }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2"><strong>{"Store Name:"}</strong> {customersQueryResult.getNestPassPurchasedCustomersByDateRange.storeName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2"><strong>{"Total Customers:"}</strong> {customersQueryResult.getNestPassPurchasedCustomersByDateRange.totalCount}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2"><strong>{"Total Purchase Amount:"}</strong> ₹{customersQueryResult.getNestPassPurchasedCustomersByDateRange.totalPurchaseAmount}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2"><strong>{"Nest Pass Duration:"}</strong> {customersQueryResult.getNestPassPurchasedCustomersByDateRange.nestPassDurationInMonths} {"Months"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2"><strong>{"Regular Price:"}</strong> ₹{customersQueryResult.getNestPassPurchasedCustomersByDateRange.nestPassRegularPurchasePrice}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2"><strong>{"Discounted Price:"}</strong> ₹{customersQueryResult.getNestPassPurchasedCustomersByDateRange.nestPassDiscountedPurchasePrice}</Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Customers Table */}
            <Box sx={{ width: '100%', maxWidth: '1500px', overflowX: 'auto', borderRadius: 'lg', boxShadow: 'md', border: '1px solid #e5e7eb' }}> {/* Increased maxWidth for table container */}
              <DataTable
                customStyles={customStyles}
                subHeader={true}
                title={<TableHeader title={"Nest Pass Sales Report"} />}
                columns={columns}
                data={tableData}
                progressPending={customersLoading}
                progressComponent={<CustomLoader />}
                paginationDefaultPage={currentPage}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                onChangeRowsPerPage={handlePerRowChange}
                onChangePage={handlePageChange}
                sortFunction={customSort}
              
              />
            </Box>
          </>
        )}
      </Container>
    </>
  );
};

export default withTranslation()(NestPassCustomersPage);
